import {
  ORBI_APPOINTMENT_ACTION,
  ORBI_CHECK_IN_MINUTES_BEFORE,
  ORBI_POST_APPOINTMENT_GRACE_MINUTES,
  OrbiAppointmentAction,
} from '@patient-ui/shared/constants';
import {
  IAppointment,
  OrbiAppointment,
  OrbiAppointmentAddress,
  OrbiAppointmentInsurance,
  OrbiFindLabData,
  OrbiPatientPassData,
} from '@patient-ui/shared/models';

/**
 * Pure mappers for the Orbi appointment-card payloads (PCSA-13241 Make an
 * Appointment / PCSA-13374 Patient Pass / PCSA-13397 Upcoming Appointment).
 *
 * The Appointments agent passes the raw `getAppointments` tool result through
 * UNMODIFIED (`AppointmentsAgent.yaml`: "Put the raw JSON result into the Data
 * field unmodified as a JSON object: `{"appointments": [...]}`"), so the shape
 * that reaches the client is the scheduling API's row, not a UI model. Mapping
 * it is exactly what mobile does in `AppointmentListItem.fromJson` /
 * `toUpcomingAppointmentData`, and these functions mirror that logic — INCLUDING
 * its field aliases, because the scheduling API surfaces the same value under
 * different keys depending on the source PSC record.
 *
 * Kept framework-free (no Angular, no Date-formatting locale APIs beyond the
 * platform ones) and side-effect free so the card components stay presentational
 * and the mapping is unit-testable on its own. Every field is optional on the
 * way out: the cards render from whatever the agent actually returned rather
 * than requiring a complete row (PCSA-13397 AC 3 lists each detail as "when
 * returned").
 */

/** Narrow an unknown value to a plain object map. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * First non-empty trimmed string among `keys` (an ALIAS lookup — the scheduling
 * row uses different key names per source record, e.g.
 * `confirmationNum` | `confirmationCode`). Numbers are stringified so a numeric
 * confirmation code still resolves; empty/whitespace collapses to undefined so
 * callers can null-check once (mobile normalizes the same way).
 */
function firstString(
  source: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
}

/**
 * Lenient coordinate parse: accepts a number or a numeric string, and treats 0
 * as ABSENT. Mobile's `parseCoord(..., treatZeroAsNull: true)` does the same —
 * the scheduling API uses 0/0 as a "no coordinates" sentinel, and passing it
 * through would point the map at the Gulf of Guinea.
 */
function parseCoord(value: unknown): number | undefined {
  const num =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim().length > 0
      ? Number(value)
      : NaN;
  if (!Number.isFinite(num) || num === 0) {
    return undefined;
  }
  return num;
}

/** Lenient integer parse for `locatorId` (may arrive as a number or string). */
function parseIntegerId(value: unknown): number | undefined {
  const num =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim().length > 0
      ? Number(value)
      : NaN;
  if (!Number.isFinite(num)) {
    return undefined;
  }
  return Math.trunc(num);
}

/**
 * `MM/DD/YYYY` + a loose time (`930a`, `09:30 AM`, `0930`) → an ISO instant in
 * LOCAL time. Follows mobile `parseSchedulingApptLocal`: the scheduling API's
 * `apptDate`/`apptTime` pair carries no timezone and denotes a wall-clock time
 * at the lab, so it must be constructed locally rather than parsed as UTC.
 * Returns undefined on anything unparseable (never a "now" fallback — an
 * appointment card must not invent a time).
 *
 * Two places where this deliberately does NOT copy mobile, both raised in review
 * on PR 3163 and both cases where mobile's version is simply wrong:
 *
 *  1. A MERIDIEM is honored. Mobile strips every non-digit before reading the
 *     time, so `01:45 PM` loses its `PM` and becomes 01:45 — a real appointment
 *     shown twelve hours early. The `a`/`p` is read here before the digits are.
 *  2. The day is verified against the MONTH. `day <= 31` passes `02/31/2026`, and
 *     `new Date` then rolls it silently forward to March 3 rather than failing —
 *     so a malformed date would render as a confident, wrong appointment. The
 *     constructed date is read back and rejected if it moved.
 */
function parseSchedulingDateTime(
  apptDate: string,
  apptTime: string | undefined
): string | undefined {
  const parts = apptDate.split('/');
  if (parts.length !== 3) {
    return undefined;
  }
  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);
  if (
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(year)
  ) {
    return undefined;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return undefined;
  }

  let hours = 0;
  let minutes = 0;
  if (apptTime && apptTime.trim().length > 0) {
    // Read the meridiem BEFORE the digits are stripped, or it is lost with them.
    // Matched on the leading letter so every form the API varies on is covered:
    // `a`, `am`, `AM`, `p.m.`, `930a`.
    const meridiem = /([ap])\.?m?\.?\s*$/i.exec(apptTime.trim())?.[1];
    const digits = apptTime.replace(/[^0-9]/g, '');
    if (digits.length > 0) {
      const normalized =
        digits.length <= 4 ? digits.padStart(4, '0') : digits.slice(0, 4);
      const parsedHours = Number(normalized.slice(0, 2));
      const parsedMinutes = Number(normalized.slice(2, 4));
      if (
        !Number.isFinite(parsedHours) ||
        !Number.isFinite(parsedMinutes) ||
        parsedHours > 23 ||
        parsedMinutes > 59
      ) {
        return undefined;
      }
      hours = parsedHours;
      minutes = parsedMinutes;

      if (meridiem) {
        // A meridiem only makes sense on a 1-12 clock. Above 12 the value is
        // already 24-hour (`1345p`), so trust the digits rather than shifting
        // them into tomorrow.
        const isPm = meridiem.toLowerCase() === 'p';
        if (hours >= 1 && hours <= 11 && isPm) {
          hours += 12;
        } else if (hours === 12 && !isPm) {
          hours = 0; // 12:xx AM is midnight
        }
      }
    }
  }

  const local = new Date(year, month - 1, day, hours, minutes);
  // Reject a date `new Date` silently rolled over (e.g. 02/31 -> Mar 3): if the
  // month or day it reports back differs from what was asked for, the input was
  // not a real calendar date.
  if (
    isNaN(local.getTime()) ||
    local.getMonth() !== month - 1 ||
    local.getDate() !== day
  ) {
    return undefined;
  }
  return local.toISOString();
}

/**
 * Resolve the appointment instant. Prefers the explicit datetime keys
 * (`appointmentDateTime` | `appointmentDateTimeUTC`, which carry a timezone),
 * then falls back to the `apptDate` + `apptTime` pair, then to a date-only
 * `apptDate` — the same precedence as mobile `_parseAppointmentDateTime`, minus
 * its `DateTime.now()` last resort (see [parseSchedulingDateTime]).
 */
function resolveAppointmentDateTime(
  row: Record<string, unknown>
): string | undefined {
  const explicit = firstString(row, [
    'appointmentDateTime',
    'appointmentDateTimeUTC',
    'dateTime',
  ]);
  if (explicit) {
    const parsed = new Date(explicit);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  const apptDate = firstString(row, ['apptDate']);
  if (apptDate) {
    return parseSchedulingDateTime(apptDate, firstString(row, ['apptTime']));
  }
  return undefined;
}

/**
 * The location's address fields under their aliases, with the two nestings the
 * scheduling API uses both tolerated (`location.address`, or the fields sitting
 * at the `location` ROOT — mobile's coordinate parse tolerates both too).
 *
 * Shared by [formatLocationAddress] and [parseRateVisitAddress] so the display
 * line and the rate-visit query params can never disagree about which key won.
 */
function parseAddressParts(location: unknown): {
  street?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
} {
  if (!isRecord(location)) {
    return {};
  }
  const address = isRecord(location['address'])
    ? location['address']
    : location;

  return {
    street: firstString(address, [
      'street',
      'addressLine1',
      'address1',
      'line1',
    ]),
    line2: firstString(address, ['line2']),
    city: firstString(address, ['city']),
    state: firstString(address, ['state', 'stateAbbr']),
    zip: firstString(address, ['zipCode', 'postalCode', 'zip']),
  };
}

/**
 * Format the location into one address line. Mirrors mobile
 * `_formatLocationAddress` (street, line2, "city, ST zip") including its key
 * aliases.
 */
function formatLocationAddress(location: unknown): string | undefined {
  const { street, line2, city, state, zip } = parseAddressParts(location);

  const stateZip = [state, zip].filter(Boolean).join(' ');
  const cityStateZip = [city, stateZip].filter(Boolean).join(', ');
  const line = [street, line2, cityStateZip].filter(Boolean).join(', ');
  return line.length > 0 ? line : undefined;
}

/**
 * The structured address the Visit History card's "Rate Visit" link needs
 * (mobile's `rateVisitAddressFromSchedulingLocation`). Returns undefined unless
 * a STREET was found: the rating page keys off street + locator id, and mobile
 * likewise refuses to build the URL without one rather than sending a partial
 * address. `line2` is deliberately dropped — the page takes a single `Address`
 * param, and mobile sends the street alone.
 */
function parseRateVisitAddress(
  location: unknown
): OrbiAppointmentAddress | undefined {
  const { street, city, state, zip } = parseAddressParts(location);
  if (!street) {
    return undefined;
  }
  const address: OrbiAppointmentAddress = { street };
  if (city) {
    address.city = city;
  }
  if (state) {
    address.state = state;
  }
  if (zip) {
    address.postalCode = zip;
  }
  return address;
}

/** Coordinates from `location.address` first, then the `location` root. */
function parseLocationCoords(location: unknown): {
  latitude?: number;
  longitude?: number;
} {
  if (!isRecord(location)) {
    return {};
  }
  const nested = isRecord(location['address'])
    ? location['address']
    : undefined;
  if (nested) {
    const nestedLatitude = parseCoord(nested['latitude']);
    const nestedLongitude = parseCoord(nested['longitude']);
    if (nestedLatitude !== undefined && nestedLongitude !== undefined) {
      return { latitude: nestedLatitude, longitude: nestedLongitude };
    }
  }
  const latitude =
    parseCoord(location['latitude']) ?? parseCoord(location['lat']);
  const longitude =
    parseCoord(location['longitude']) ??
    parseCoord(location['long']) ??
    parseCoord(location['lng']);
  if (latitude !== undefined && longitude !== undefined) {
    return { latitude, longitude };
  }
  return {};
}

/**
 * Map the row's `insurances[]` to the two eligibility signals the card uses.
 *
 * `eligible` is ALIASED to `insuranceSearchEligible`, the key the portal stitches
 * onto its own appointment rows from `/me/insurances/search`
 * (`PatientEffects.processInsuranceChecks`). The scheduling row the agent passes
 * through carries neither, so this only pays off for a caller handing in an
 * already-enriched row — but when one does, the card reads the same flag the
 * Appointments page branches on instead of silently ignoring it (PCSA-13543).
 */
function parseInsurances(
  value: unknown
): OrbiAppointmentInsurance[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const records = value.filter(isRecord).map((record) => {
    const insurance: OrbiAppointmentInsurance = {};
    if (typeof record['evOverride'] === 'boolean') {
      insurance.evOverride = record['evOverride'];
    }
    const eligible = record['eligible'] ?? record['insuranceSearchEligible'];
    if (typeof eligible === 'boolean') {
      insurance.eligible = eligible;
    }
    return insurance;
  });
  return records.length > 0 ? records : [];
}

/**
 * Map ONE raw appointment row to the typed [OrbiAppointment] the card renders.
 * Mirrors mobile `AppointmentListItem.fromJson(...).toUpcomingAppointmentData()`.
 */
export function mapOrbiAppointment(
  row: Record<string, unknown>
): OrbiAppointment {
  const location = row['location'];
  const coords = parseLocationCoords(location);
  const appointment: OrbiAppointment = {};

  const confirmationNumber = firstString(row, [
    'confirmationNum',
    'confirmationCode',
    'confirmationNumber',
  ]);
  if (confirmationNumber) {
    appointment.confirmationNumber = confirmationNumber;
  }

  const dateTime = resolveAppointmentDateTime(row);
  if (dateTime) {
    appointment.dateTime = dateTime;
  }

  const displayDateTimeText = firstString(row, ['displayDateTimeText']);
  if (displayDateTimeText) {
    appointment.displayDateTimeText = displayDateTimeText;
  }

  const reasonForVisit = firstString(row, [
    'serviceName',
    'serviceType',
    'reasonForVisit',
  ]);
  if (reasonForVisit) {
    appointment.reasonForVisit = reasonForVisit;
  }

  const locationAddress =
    formatLocationAddress(location) ?? firstString(row, ['locationAddress']);
  if (locationAddress) {
    appointment.locationAddress = locationAddress;
  }

  const locatorId =
    parseIntegerId(isRecord(location) ? location['locatorId'] : undefined) ??
    parseIntegerId(row['locatorId']);
  if (locatorId !== undefined) {
    appointment.locatorId = locatorId;
  }

  const rateVisitAddress = parseRateVisitAddress(location);
  if (rateVisitAddress) {
    appointment.rateVisitAddress = rateVisitAddress;
  }

  if (coords.latitude !== undefined) {
    appointment.latitude = coords.latitude;
  }
  if (coords.longitude !== undefined) {
    appointment.longitude = coords.longitude;
  }

  const paidBy = firstString(row, ['paidBy']);
  if (paidBy) {
    appointment.paidBy = paidBy;
  }

  const lpid = firstString(row, ['lpid']);
  if (lpid) {
    appointment.lpid = lpid;
  }

  if (row['past'] === true || row['isPast'] === true) {
    appointment.isPast = true;
  }

  if (isCheckedInRow(row)) {
    appointment.isCheckedIn = true;
  }

  const insurances = parseInsurances(row['insurances']);
  if (insurances) {
    appointment.insurances = insurances;
  }

  const actions = parseActions(row);
  if (actions) {
    appointment.actions = actions;
  }

  return appointment;
}

/**
 * Whether the row states that this appointment has already been checked into
 * (PCSA-13577).
 *
 * OPPORTUNISTIC. The scheduling list row the agent passes through
 * (`IAppointment`) has no status field at all — only `past` — so in practice this
 * returns false and the state arrives via [applyOrbiCheckedIn] instead. It is
 * read anyway because the SAME appointment resource does carry it on the detail
 * endpoint (`AppointmentDetailsResponse.status`, whose 'checkedin' value the
 * portal's own check-in widget branches on), so a payload that ever comes from
 * there lights the state up with no further change.
 *
 * Value matching is separator- and case-insensitive, like [normalizeAction] and
 * for the same reason: the shape is not pinned by a contract, so 'checkedin',
 * 'CheckedIn', 'CHECKED_IN' and 'Checked In' all have to count. A boolean
 * `checkedIn` key is honored too — that is how mobile's own snapshot names it.
 */
function isCheckedInRow(row: Record<string, unknown>): boolean {
  if (row['checkedIn'] === true || row['isCheckedIn'] === true) {
    return true;
  }
  const status = firstString(row, [
    'status',
    'appointmentStatus',
    'apptStatus',
  ]);
  return (
    !!status && status.toLowerCase().replace(/[^a-z]/g, '') === 'checkedin'
  );
}

/**
 * The agent's available-actions list, when the row carried one (PCSA-13397 AC
 * 6/8). Accepts the aliases a passthrough payload might use and tolerates
 * objects shaped `{ action: 'check-in' }` / `{ type: … }` / `{ id: … }`, since
 * the contract does not yet pin the element shape. Returns undefined when the key
 * is absent (nothing was stated) and [] when it was present but empty (an
 * explicit "no actions") — [resolveOrbiAppointmentActions] treats those
 * differently.
 */
function parseActions(row: Record<string, unknown>): string[] | undefined {
  const raw =
    row['actions'] ?? row['availableActions'] ?? row['appointmentActions'];
  if (!Array.isArray(raw)) {
    return undefined;
  }
  return raw
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry.trim();
      }
      if (isRecord(entry)) {
        return firstString(entry, ['action', 'type', 'name', 'id']) ?? '';
      }
      return '';
    })
    .filter((action) => action.length > 0);
}

/**
 * Map the parser's flattened `structuredData.appointments` list to typed
 * appointments, dropping any row that yielded nothing renderable (no date/time
 * AND no address — such a card would be an empty shell).
 */
export function mapOrbiAppointments(
  rows: Record<string, unknown>[] | undefined
): OrbiAppointment[] {
  return (rows ?? [])
    .filter(isRecord)
    .map(mapOrbiAppointment)
    .filter(
      (appointment) =>
        !!appointment.dateTime ||
        !!appointment.displayDateTimeText ||
        !!appointment.locationAddress
    );
}

/**
 * Read the typed `AppointmentPatientPass` payload out of the turn's raw `data`
 * (PCSA-13374). The agent sends a single map, but tolerate an array wrapper
 * (first element) the way the rest of the parser does.
 */
export function mapOrbiPatientPassData(
  raw: Record<string, unknown>[] | Record<string, unknown> | undefined
): OrbiPatientPassData {
  const source = Array.isArray(raw) ? raw.find(isRecord) : raw;
  if (!isRecord(source)) {
    return {};
  }
  const data: OrbiPatientPassData = {};
  const lpid = firstString(source, ['lpid', 'Lpid', 'LPID']);
  if (lpid) {
    data.lpid = lpid;
  }
  const confirmationNumber = firstString(source, [
    'confirmationNumber',
    'confirmationNum',
    'confirmationCode',
  ]);
  if (confirmationNumber) {
    data.confirmationNumber = confirmationNumber;
  }
  return data;
}

/**
 * The insurance task outstanding on an appointment (PCSA-13397 AC 5). Derived
 * from the FIRST insurance record, matching BOTH platforms' existing rule —
 * mobile `UpcomingAppointmentData.insuranceTaskState` and web
 * `AppointmentButtonConfigService`, which reads `insuranceInfo[0]`:
 *  - not scheduled with insurance → `none`
 *  - scheduled with insurance, no records → `provide`
 *  - explicitly ineligible → `review`
 *  - a record on file and nothing to fix → `update`
 *
 * `checking` is still part of the type (a caller may hold an appointment whose
 * eligibility check it knows is in flight) but is no longer produced here — see
 * `resolveOrbiInsuranceTask` for why the agent payload can never distinguish it.
 */
export type OrbiInsuranceTask =
  | 'none'
  | 'provide'
  | 'review'
  | 'checking'
  | 'update';

/** True when the appointment was scheduled with insurance (`paidBy`). */
export function isOrbiInsuranceAppointment(
  appointment: OrbiAppointment
): boolean {
  return (appointment.paidBy ?? '').toLowerCase().includes('insurance');
}

/**
 * Resolve the outstanding insurance task — see [OrbiInsuranceTask].
 *
 * A record ON FILE with no eligibility problem resolves to `update`, not to
 * `none`. Two reasons, and QA hit both:
 *
 *  - Verified insurance still has a task worth offering — "this is what we have
 *    on file; change it if it is wrong" (PCSA-13544: "Update Insurance is not
 *    rendered when the insurance was reviewed/verified"). The Appointments page
 *    drops the CTA in that state, but it has a whole row of controls to fall back
 *    on; the chat card would show a lone Details button.
 *  - The ELIGIBILITY signal is not in this payload at all, and cannot be. The
 *    appointment row carries `evOverride` only; `insuranceSearchEligible` — the
 *    flag `AppointmentButtonConfigService` branches on — is not a scheduling
 *    field, it is the portal's own `/me/insurances/search` result, stitched onto
 *    the row by `processInsuranceChecks` after `loadPatientAppointments`. So the
 *    old `checking` fallback was every real agent payload's answer, and it renders
 *    NOTHING: an insurance appointment with insurance on file got no insurance CTA
 *    on the card while the Appointments page showed one (PCSA-13543).
 *
 * `review` therefore only comes from an explicit `eligible: false`, which is what
 * arrives when a caller enriched the row from that search (or the agent starts
 * returning it). Until then the card offers the neutral flow rather than silence.
 */
export function resolveOrbiInsuranceTask(
  appointment: OrbiAppointment
): OrbiInsuranceTask {
  if (!isOrbiInsuranceAppointment(appointment)) {
    return 'none';
  }
  const records = appointment.insurances;
  if (records === undefined) {
    // Scheduled with insurance but the row carried no `insurances` key at all —
    // we cannot tell whether one is on file, so offer the neutral "Update
    // Insurance" action rather than asserting the user must provide one.
    return 'update';
  }
  if (records.length === 0) {
    return 'provide';
  }
  if (records[0].eligible === false) {
    return 'review';
  }
  return 'update';
}

/**
 * The portal's own insurance-eligibility answer per appointment, keyed by
 * confirmation number: `true` = eligible, `false` = the state the Appointments
 * page labels "Review Insurance". An appointment absent from the map has no
 * answer (never checked, not an insurance appointment, or the check is still in
 * flight) and is left exactly as the payload described it.
 */
export type OrbiInsuranceEligibilityMap = Readonly<Record<string, boolean>>;

/**
 * Build that map from the portal's OWN appointment rows (PCSA-13543).
 *
 * The eligibility signal the Appointments page branches on is not a scheduling
 * field and so can never arrive in an agent payload: it is the
 * `/me/insurances/search` result that `PatientEffects.processInsuranceChecks`
 * stitches onto each row after `loadPatientAppointments`. QA compared the two
 * surfaces side by side — the Appointments page offered "Review Insurance" and
 * the chat card offered nothing — so the card reads the same answer from the
 * store rather than guessing from the row it was handed.
 *
 * The rule is `AppointmentButtonConfigService.hasIneligibleInsurance` /
 * `isCheckingInsuranceEligibility`, stated once here so the two surfaces cannot
 * drift:
 *  - `evOverride: true` → eligible, whatever the search said (the lab overrode
 *    verification for this appointment).
 *  - `evOverride: false` + `insuranceSearchEligible: false` → ineligible.
 *  - `evOverride: false` + no search result yet → OMITTED, not `true`. The check
 *    is in flight; claiming eligibility would flip the card's action for a moment
 *    and then flip it back.
 * Rows with no insurance on file are omitted too — "which insurance is on file"
 * is something the payload itself carries, so there is nothing to import.
 */
export function buildOrbiInsuranceEligibility(
  appointments: readonly IAppointment[] | undefined | null
): OrbiInsuranceEligibilityMap {
  const eligibility: Record<string, boolean> = {};
  (appointments ?? []).forEach((appointment) => {
    const confirmationNumber = (appointment.confirmationNum ?? '').trim();
    const records = appointment.insuranceInfo;
    if (!confirmationNumber || !records || records.length === 0) {
      return;
    }
    const record = records[0];
    if (record.evOverride === true) {
      eligibility[confirmationNumber] = true;
      return;
    }
    if (typeof record.insuranceSearchEligible === 'boolean') {
      eligibility[confirmationNumber] = record.insuranceSearchEligible;
    }
  });
  return eligibility;
}

/**
 * Fold the portal's eligibility answers into mapped appointment rows, so
 * [resolveOrbiInsuranceTask] sees what the Appointments page sees (PCSA-13543).
 *
 * The PAYLOAD wins where it spoke: a row that already carried an `eligible`
 * boolean (an agent that started returning it, or a caller that enriched the row
 * itself) keeps its own value, and a row whose `insurances` key is an explicit
 * empty list keeps that too — that is the agent stating "nothing on file", which
 * is the `provide` case, and inventing a record here would replace an answer with
 * an inference.
 *
 * Returns the SAME array identity when nothing changed, so the card's OnPush
 * input check still short-circuits.
 */
export function applyOrbiInsuranceEligibility(
  appointments: OrbiAppointment[],
  eligibility: OrbiInsuranceEligibilityMap | undefined
): OrbiAppointment[] {
  if (!eligibility || Object.keys(eligibility).length === 0) {
    return appointments;
  }
  let changed = false;
  const enriched = appointments.map((appointment) => {
    const confirmationNumber = appointment.confirmationNumber;
    if (!confirmationNumber || !(confirmationNumber in eligibility)) {
      return appointment;
    }
    const records = appointment.insurances;
    if (records?.length === 0 || records?.[0].eligible !== undefined) {
      return appointment;
    }
    changed = true;
    const first: OrbiAppointmentInsurance = {
      ...(records?.[0] ?? {}),
      eligible: eligibility[confirmationNumber],
    };
    return {
      ...appointment,
      insurances: [first, ...(records ?? []).slice(1)],
    };
  });
  return changed ? enriched : appointments;
}

/**
 * Fold the portal's own record of completed Mobile Check-ins into mapped rows, so
 * the card shows the disabled "Checked In" pill after the patient checks in
 * (PCSA-13577).
 *
 * This is the source that actually fires. The check-in POST is the only place the
 * web learns the answer: the scheduling list row the agent passes through carries
 * no status (see [isCheckedInRow]), the portal's own appointment slice
 * (`IAppointment`) carries none either, and reading the per-appointment detail
 * endpoint would mean one extra request per row on every card — which is the
 * fan-out the panel deliberately avoids. So the check-in call records the
 * confirmation number and the codes are handed in here, which is exactly how
 * mobile does it (`AppointmentsService._mobileCheckedInConfirmationCodes`, added
 * on the POST's success and cleared on sign-out).
 *
 * Consequence, stated plainly: the state is scoped to the browser session that
 * performed the check-in. It covers the reported defect (check in, then open the
 * chat) and every path within that session, including a reload. A patient who
 * checks in and returns tomorrow sees "Mobile Check-in" again — the same limit
 * mobile ships with, and it cannot be lifted from the frontend until the
 * appointment list returns a status.
 *
 * A row the payload already marked keeps its mark. Returns the SAME array
 * identity when nothing changed, so the card's OnPush input check still
 * short-circuits.
 */
export function applyOrbiCheckedIn(
  appointments: OrbiAppointment[],
  confirmationNumbers: readonly string[] | undefined
): OrbiAppointment[] {
  if (!confirmationNumbers || confirmationNumbers.length === 0) {
    return appointments;
  }
  const codes = new Set(confirmationNumbers.map((code) => code.trim()));
  let changed = false;
  const enriched = appointments.map((appointment) => {
    const confirmationNumber = appointment.confirmationNumber?.trim();
    if (
      appointment.isCheckedIn ||
      !confirmationNumber ||
      !codes.has(confirmationNumber)
    ) {
      return appointment;
    }
    changed = true;
    return { ...appointment, isCheckedIn: true };
  });
  return changed ? enriched : appointments;
}

/**
 * Normalize one agent-supplied action string to a known token, or undefined when
 * it names something the card cannot render. Case- and separator-insensitive
 * (`Mobile Check-in`, `MOBILE_CHECKIN`, `checkIn` → `check-in`) because the
 * contract does not pin the casing the agent will use.
 */
function normalizeAction(value: string): OrbiAppointmentAction | undefined {
  const key = value.toLowerCase().replace(/[^a-z]/g, '');
  switch (key) {
    case 'details':
    case 'appointmentdetails':
      return ORBI_APPOINTMENT_ACTION.details;
    case 'checkin':
    case 'mobilecheckin':
      return ORBI_APPOINTMENT_ACTION.checkIn;
    case 'provideinsurance':
      return ORBI_APPOINTMENT_ACTION.provideInsurance;
    case 'reviewinsurance':
      return ORBI_APPOINTMENT_ACTION.reviewInsurance;
    case 'updateinsurance':
      return ORBI_APPOINTMENT_ACTION.updateInsurance;
    default:
      return undefined;
  }
}

/**
 * Whether Mobile Check-in is open for this appointment at `now` (PCSA-13520).
 *
 * The window is `[start - 30 min, start + 15 min]` in the USER'S LOCAL time —
 * `appointment.dateTime` is already resolved to a local instant by
 * [resolveAppointmentDateTime], since the scheduling row's `apptDate`/`apptTime`
 * pair is a wall-clock time at the lab. It is mobile's window
 * (`isMobileCheckInAvailable`: `kMobileCheckInMinutesBefore = 30`,
 * `kPostAppointmentPassVisibilityMinutes = 15`), and it is computed here rather
 * than taken from the agent.
 *
 * That is a DELIBERATE override of AC 6.1 / AC 8 / AC 1.2, which put check-in
 * availability on the agent and say the frontend must not calculate the cutoff.
 * Traced end to end on PCSA-13520, that capability does not exist and is not
 * planned: nothing in `moonshot-ai-api` emits an appointment `actions` list,
 * `AppointmentsAgent.yaml` mandates the `/me` scheduling row be passed through
 * "unmodified", and that row (`IAppointment`) has no actions field — check-in is a
 * separate return type there (`AppointmentPatientPass`). So the button was
 * structurally unreachable, which is the defect. Mobile does not wait for an agent
 * either: its dashboard tile computes exactly this window client-side.
 *
 * The window is derived from the SAME state machine as the card's title
 * ([resolveAppointmentTimeState]), so the button and the "Appointment in N
 * Minutes" / "Appointment at 5:50 PM" states cannot disagree — one card saying
 * both "Upcoming Appointment" and "Mobile Check-in" would be a defect of its own.
 *
 * A confirmation number is required because it is the whole payload of the flow
 * (`AppointmentActionsService.navigateToCheckIn`); without one the button can only
 * drop the user on the appointments list, which the card already offers.
 */
export function isOrbiMobileCheckInAvailable(
  appointment: OrbiAppointment,
  now: Date = new Date()
): boolean {
  return (
    !appointment.isPast &&
    !!appointment.confirmationNumber &&
    resolveAppointmentTimeState(appointment, now) !== null
  );
}

/**
 * The actions to render on an appointment card (PCSA-13397 AC 4/5/6/8), in the
 * order the approved Figma draws them: check-in, Appointment Details, then the
 * insurance task.
 *
 * AC 6 says an action shows only when it is "returned as available for that
 * appointment". Availability is the union of three sources, each of which is
 * first-hand evidence rather than a guess:
 *  - Whatever the `actions` list named, filtered to the tokens the card can route
 *    (an unknown action would render a dead button). This is the AC-1.2 path.
 *  - Plus the actions the payload each one NEEDS is carrying:
 *      - `details` — needs `confirmationNumber` (the route parameter).
 *      - insurance — one of provide/review/update per [resolveOrbiInsuranceTask],
 *        and only for an insurance-paid appointment.
 *  - Plus `check-in`, from the clock, per [isOrbiMobileCheckInAvailable] — and
 *    ONLY from the clock while the patient has not checked in. An agent-named
 *    `check-in` outside the window is dropped, so the rule has exactly one source:
 *    the button is shown iff the appointment is inside
 *    `[start - 30 min, start + 15 min]` locally. The agent cannot know the
 *    patient's timezone, so its list is strictly the less informed of the two, and
 *    honoring both would let them contradict each other on the same card. Once
 *    `isCheckedIn` is set the slot is unconditional instead, holding the disabled
 *    "Checked In" pill (PCSA-13577) — see the branch below.
 *
 * The second bullet used to apply ONLY when the payload carried no `actions` key,
 * on the AC-6 reading that a returned list is exhaustive. It is not: the shipped
 * agent names the flows it owns and says nothing about the insurance task, so a row
 * whose own `insurances` record needed review or an update rendered that CTA on the
 * Appointments page and nothing at all on the chat card (PCSA-13543, PCSA-13544).
 * The card computes that state from the same record `AppointmentButtonConfigService`
 * does, so the payload is first-hand evidence, not a guess.
 *
 * An EMPTY `actions` array still means exactly what the contract says it means — an
 * explicit "no actions" — and suppresses everything, the clock included. A PAST
 * appointment (VisitHistory) is not actionable either way —
 * `AppointmentsAgent.yaml`: "Past appointments cannot be edited, rescheduled,
 * canceled, or checked into."
 *
 * `now` is injected for the same reason the title's is: the row is time-dependent,
 * and the card passes its own `now` Input so every row on it agrees on the instant.
 */
export function resolveOrbiAppointmentActions(
  appointment: OrbiAppointment,
  now: Date = new Date()
): OrbiAppointmentAction[] {
  if (appointment.isPast || appointment.actions?.length === 0) {
    return [];
  }

  const available = new Set<OrbiAppointmentAction>(
    (appointment.actions ?? [])
      .map(normalizeAction)
      .filter((action): action is OrbiAppointmentAction => !!action)
  );

  // Single-sourced on the clock: added when the window is open, removed when it is
  // not, whatever the agent said — EXCEPT once the patient has checked in, when
  // the slot is held by the disabled "Checked In" pill and the clock stops
  // deciding (PCSA-13577). The Figma frame draws that state on a plain "Upcoming
  // Appointment" card, i.e. outside the window, and taking the pill away as the
  // window closes would tell a patient who HAS checked in that they have not. No
  // confirmation number is required for it either: nothing routes anywhere from a
  // disabled button.
  if (
    appointment.isCheckedIn ||
    isOrbiMobileCheckInAvailable(appointment, now)
  ) {
    available.add(ORBI_APPOINTMENT_ACTION.checkIn);
  } else {
    available.delete(ORBI_APPOINTMENT_ACTION.checkIn);
  }

  if (appointment.confirmationNumber) {
    available.add(ORBI_APPOINTMENT_ACTION.details);
  }
  switch (resolveOrbiInsuranceTask(appointment)) {
    case 'provide':
      available.add(ORBI_APPOINTMENT_ACTION.provideInsurance);
      break;
    case 'review':
      available.add(ORBI_APPOINTMENT_ACTION.reviewInsurance);
      break;
    case 'update':
      available.add(ORBI_APPOINTMENT_ACTION.updateInsurance);
      break;
    // 'checking' — the carrier check is still in flight, and 'none' — nothing
    // outstanding. Neither surfaces an insurance CTA (mobile shows a passive
    // "Checking…" strip; the chat card simply omits the action).
    default:
      break;
  }
  return ORBI_APPOINTMENT_ACTION_ORDER.filter((action) =>
    available.has(action)
  );
}

/**
 * Render order for the action row, from the approved Figma: the check-in control
 * leads whenever it is offered, live or spent, Appointment Details follows, and
 * the insurance task trails.
 *
 * Order also decides emphasis, because the card fills whichever action LEADS the
 * row and outlines the rest (`OrbiAppointmentCardComponent.actionVariant`). So
 * this list is what makes the filled pill land on check-in inside its window and
 * on Appointment Details the rest of the time — which is the pair of frames the
 * design draws.
 */
const ORBI_APPOINTMENT_ACTION_ORDER: OrbiAppointmentAction[] = [
  ORBI_APPOINTMENT_ACTION.checkIn,
  ORBI_APPOINTMENT_ACTION.details,
  ORBI_APPOINTMENT_ACTION.provideInsurance,
  ORBI_APPOINTMENT_ACTION.reviewInsurance,
  ORBI_APPOINTMENT_ACTION.updateInsurance,
];

/**
 * The card's title, reflecting the appointment's state (PCSA-13397 AC 2):
 *  - "Appointment in N Minutes" inside the check-in window (<= 30 min before
 *    start, same day), with the singular "Minute" at exactly 1 (mobile
 *    `_getTimeAtTopLabel`).
 *  - "Appointment at 5:50 PM" once the start has passed but is still within the
 *    15-minute grace period.
 *  - "Upcoming Appointment" otherwise (the standard state), and "Past Visit" for
 *    a VisitHistory row.
 *
 * The two time-bound states drop the "Upcoming" stem and title-case the unit.
 * They used to keep it ("Upcoming Appointment in 17 minutes") on the reading that
 * AC 2's shorter forms were examples and that one stem made the three states read
 * as one card; QA rejected both strings against the design (PCSA-13520 asks for
 * "Appointment in [X] Minutes", PCSA-13521 for "Appointment at 5:50 PM"), so
 * AC 2's wording is now taken literally. "Upcoming Appointment" remains the
 * standard title — nothing in QA's findings touches it.
 *
 * `now` is injected so the caller (and the tests) control the clock rather than
 * this function reading it — the title is time-dependent, and a card must not
 * silently disagree with the turn it was rendered in.
 */
/**
 * Where the appointment sits relative to `now`, or null for the standard
 * "upcoming" state (too far out, already past the grace period, or no parseable
 * time at all).
 *
 * The one place the two time windows are defined. Both the card's title and Mobile
 * Check-in's availability read it, so the countdown and the button are the same
 * decision rather than two implementations of the same rule that can drift
 * (PCSA-13520).
 *
 * `imminent` keeps the SAME-DAY guard the title has always had. Read against the
 * 30-minute bound it is redundant — nothing 30 minutes away is on another date —
 * except across midnight, where it suppresses both the countdown and the button.
 * That case needs a lab open at 00:00, so it is unreachable in practice, and the
 * conservative direction: no button offered rather than one offered wrongly.
 */
function resolveAppointmentTimeState(
  appointment: OrbiAppointment,
  now: Date
):
  | { kind: 'imminent'; minutesUntilStart: number }
  | { kind: 'started'; start: Date }
  | null {
  const start = appointment.dateTime ? new Date(appointment.dateTime) : null;
  if (!start || isNaN(start.getTime())) {
    return null;
  }

  const msUntilStart = start.getTime() - now.getTime();

  if (msUntilStart > 0) {
    const sameDay =
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === now.getDate();
    const minutesUntilStart = Math.ceil(msUntilStart / 60_000);
    return sameDay && minutesUntilStart <= ORBI_CHECK_IN_MINUTES_BEFORE
      ? { kind: 'imminent', minutesUntilStart }
      : null;
  }

  // Started already: inside the grace period the card has something to say, after
  // it the appointment is just an appointment again.
  const minutesSinceStart = -msUntilStart / 60_000;
  return minutesSinceStart <= ORBI_POST_APPOINTMENT_GRACE_MINUTES
    ? { kind: 'started', start }
    : null;
}

export function orbiAppointmentCardTitle(
  appointment: OrbiAppointment,
  now: Date = new Date()
): string {
  if (appointment.isPast) {
    return 'Past Visit';
  }

  const state = resolveAppointmentTimeState(appointment, now);
  if (!state) {
    return 'Upcoming Appointment';
  }
  if (state.kind === 'imminent') {
    const unit = state.minutesUntilStart === 1 ? 'Minute' : 'Minutes';
    return `Appointment in ${state.minutesUntilStart} ${unit}`;
  }
  return `Appointment at ${formatOrbiAppointmentTime(state.start)}`;
}

/**
 * The time-bound half of `orbiAppointmentCardTitle`, or `null` when the
 * appointment is in neither time window.
 *
 * A card that renders a SET shows the pluralized set heading ("Upcoming
 * Appointments"), so before this existed the imminent appointment inside a set had
 * nowhere to say so and the countdown simply never appeared — QA hit exactly that
 * (PCSA-13520: the card "keeps showing Upcoming Appointments" 30 minutes out,
 * which only happens with two or more appointments in the payload). The per-row
 * state label closes that gap; the single-appointment card keeps carrying it in
 * the heading, where the design puts it.
 *
 * Returns null rather than 'Upcoming Appointment' for the standard state: on a row
 * the date line already says when the appointment is, so a second line repeating
 * the heading is noise.
 */
export function orbiAppointmentStateLabel(
  appointment: OrbiAppointment,
  now: Date = new Date()
): string | null {
  if (appointment.isPast) {
    return null;
  }
  const title = orbiAppointmentCardTitle(appointment, now);
  return title === 'Upcoming Appointment' ? null : title;
}

/**
 * `h:mm A` (e.g. `9:05 AM`) — the meridiem UPPERCASE and separated by a space.
 *
 * This is the STATE LABEL's time, not the date line's: it feeds "Appointment at
 * 5:50 PM", which QA specified in that exact casing with a screenshot
 * (PCSA-13521). The date line closes the meridiem up and lowercases it
 * (`formatOrbiCardTime`), so the two deliberately differ — see
 * `formatOrbiAppointmentDateTime`.
 */
export function formatOrbiAppointmentTime(date: Date): string {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  return `${hours12}:${minutes} ${meridiem}`;
}

/**
 * `h:mma` (e.g. `2:50pm`) — meridiem lowercase and CLOSED UP, no space.
 *
 * Mobile's own card date line does this by formatting `h:mma` and then
 * lowercasing the result (`_buildDateTime`:
 * `raw.replaceAll('AM','am').replaceAll('PM','pm')`), which is the casing the
 * cards are specified in. Written out here rather than reusing
 * `formatOrbiAppointmentTime` and lowercasing it, because that function is the
 * state label's and has to keep saying `5:50 PM`.
 */
function formatOrbiCardTime(date: Date): string {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = hours24 >= 12 ? 'pm' : 'am';
  return `${hours12}:${minutes}${meridiem}`;
}

/**
 * The card's date/time line (PCSA-13397 AC 3.1). Formats the RESOLVED INSTANT as
 * "Weekday, Mon. D, YYYY at h:mma" — "Thursday, Jan. 8, 2026 at 2:50pm" — and
 * falls back to the agent's pre-formatted `displayDateTimeText` only when there is
 * no parseable instant to format.
 *
 * This is mobile's `_compactDateFormat`
 * (`DateFormat("EEEE, MMM. d, yyyy 'at' h:mm a")`) for the date, with the
 * lowercase closed-up meridiem mobile's `_buildDateTime` applies to the time —
 * the format the cards are specified in for check parity with the app.
 *
 * It went out briefly without the month's period and with an uppercase spaced
 * meridiem, on a reading of design's "day, three-letter month, date, year" note
 * (PCSA-13312, 08/21). That note is the IN-CHAT conversation date, a different
 * surface with a different spec ("Today, Aug 21, 2026") — the cards follow
 * mobile. Mobile's other, long-month `_dateTimeFormat` belongs to its
 * full-screen card and is not this surface either.
 *
 * The instant WINS over `displayDateTimeText`, where it used to lose to it. Two
 * reasons, and the first is a defect:
 *
 *  1. The two are not on the same clock. `displayDateTimeText` is formatted
 *     upstream and arrives as opaque text with no timezone attached, while every
 *     time-dependent DECISION on the card reads `dateTime` through the browser's
 *     zone — the countdown and Mobile Check-in's window
 *     ([resolveAppointmentTimeState]), and the state label. So a row carrying both
 *     could show one wall-clock time and be judged against another: the line reads
 *     "at 12:55pm" while the code places the same appointment nine hours away, and
 *     Mobile Check-in is then withheld (or offered) against a time nobody on screen
 *     can see. Formatting the instant makes the line and the window the SAME
 *     number by construction rather than two readings that happen to agree.
 *  2. Design was asked which of the two shapes this line should carry and answered
 *     that the derived one is "the correct format" (08/23, see the spec) — so
 *     deferring to upstream text was also deferring on the format.
 *
 * `displayDateTimeText` stays as the fallback because it is the only thing an
 * instant-less row has: the agent's relative phrasings ("Next Tuesday") come from
 * records with no date/time keys to resolve, and the alternative there is a blank
 * line.
 */
export function formatOrbiAppointmentDateTime(
  appointment: OrbiAppointment
): string {
  const start = appointment.dateTime ? new Date(appointment.dateTime) : null;
  if (!start || isNaN(start.getTime())) {
    return appointment.displayDateTimeText ?? '';
  }
  const weekday = ORBI_WEEKDAYS[start.getDay()];
  const month = ORBI_MONTHS_ABBREVIATED[start.getMonth()];
  return `${weekday}, ${month} ${start.getDate()}, ${start.getFullYear()} at ${formatOrbiCardTime(
    start
  )}`;
}

const ORBI_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Three-letter month abbreviations WITH a trailing period — "Jan. 8, 2026".
 *
 * The period is mobile's, and deliberately on all twelve: its pattern is the
 * literal `MMM.`, so `intl` fills three letters and the string supplies the dot
 * for every month. That does make the five months English would not abbreviate
 * read oddly — "May. 8", "Mar. 8", "Apr. 8", "Jun. 8", "Jul. 8" — but check
 * parity is what these cards are specified against, and a table that abbreviated
 * only Jan/Feb/Aug/Sept/Oct/Nov/Dec would say something different from the app
 * for seven months of the year rather than five.
 *
 * The in-chat conversation date is NOT this list: design's wording there is
 * "day, three-letter month, date, year" with no period ("Friday, Aug 14, 2026",
 * PCSA-13312 08/21). It has its own table — `ORBI_CHAT_DATE_MONTHS` in
 * `orbi-history.util.ts` — and the two are meant to stay apart.
 */
const ORBI_MONTHS_ABBREVIATED = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May.',
  'Jun.',
  'Jul.',
  'Aug.',
  'Sep.',
  'Oct.',
  'Nov.',
  'Dec.',
];

/**
 * Read the typed `AppointmentFindLab` payload out of the turn's raw `data`
 * (PCSA-13241). Every field is optional in the agent contract and `data: {}` is
 * a valid response (AC 3.2), so this returns an empty object rather than
 * failing — the card then renders an empty, editable form.
 */
export function mapOrbiFindLabData(
  raw: Record<string, unknown>[] | Record<string, unknown> | undefined
): OrbiFindLabData {
  const source = Array.isArray(raw) ? raw.find(isRecord) : raw;
  if (!isRecord(source)) {
    return {};
  }
  const data: OrbiFindLabData = {};
  const initialLocation = firstString(source, [
    'initialLocation',
    'initialAddress',
  ]);
  if (initialLocation) {
    data.initialLocation = initialLocation;
  }
  const latitude = parseCoord(source['initialSearchLatitude']);
  if (latitude !== undefined) {
    data.initialSearchLatitude = latitude;
  }
  const longitude = parseCoord(source['initialSearchLongitude']);
  if (longitude !== undefined) {
    data.initialSearchLongitude = longitude;
  }
  const initialService = firstString(source, ['initialService']);
  if (initialService) {
    data.initialService = initialService;
  }
  const initialDate = parseIsoCalendarDate(
    firstString(source, ['initialDate'])
  );
  if (initialDate) {
    data.initialDate = initialDate;
  }
  return data;
}

/** Rejects impossible days such as `2026-02-30`. */
export function parseIsoCalendarDate(
  value: string | undefined
): string | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
    ? value
    : undefined;
}
